import { useEffect, useState } from 'react';

import { TFunction } from 'i18next';
import { Trans } from 'react-i18next';

import { displayToastError, displayToastSuccess } from '@/helpers';
import { RefreshData } from '@/modules/common/components/organisms/refresh-data';
import { EntityType } from '@/modules/common/types/entity-type';
import { IntegrationSyncingStatus } from '@/modules/common/types/integration-status';
import { PollingInterval } from '@/modules/common/types/polling-interval';
import { useIntegrationStatus } from '@/modules/onboarding/integrations-setup/hooks/use-integration-status';
import { useSynchronizeIntegration } from '@/modules/settings/hooks';

export interface EntityIntegrationsRefreshCardProps {
  entityType: EntityType;

  /**
   * Whether the refresh action is disabled (e.g., in read-only mode)
   */
  disabled?: boolean;

  /**
   * Localized strings for the component
   */
  strings: {
    /**
     * Button text when idle (e.g., "Refresh projects")
     */
    buttonTitle: string;

    /**
     * Button text when refreshing (e.g., "Refreshing...")
     */
    buttonTitleRefreshing: string;

    /**
     * Description text with refresh instructions
     * Should be an i18n key for Trans component
     * Example: "details.tabs.integrations.refreshAlert"
     */
    descriptionI18nKey: string;

    /**
     * Text for the refresh button placeholder in description
     * Example: "Refresh projects"
     */
    refreshButtonText: string;

    /**
     * Success toast message (e.g., "Projects refreshed successfully")
     */
    successToast: string;

    /**
     * Error toast message (e.g., "Failed to refresh projects")
     */
    errorToast: string;
  };

  /**
   * Translation function from useTranslation hook
   */
  t: TFunction;
}

/**
 * Reusable component for refreshing entity integrations data.
 * Handles synchronization of integrations and displays refresh status.
 * Can be used for projects, clients, team members, etc.
 * Tracks refresh timestamp internally.
 */
export const EntityIntegrationsRefreshCard = ({
  entityType,
  disabled,
  strings,
  t,
}: EntityIntegrationsRefreshCardProps) => {
  // Track last refresh timestamp internally
  const [isRefreshing, setIsRefreshing] = useState(false);

  // Use integration status hook to get current status
  // Use SHORT polling interval when refreshing for real-time updates
  const { data: integrationStatusData } = useIntegrationStatus({
    pollingInterval: isRefreshing ? PollingInterval.SHORT : PollingInterval.LONG,
  });

  // Use synchronize integration hook for refreshing
  const synchronizeMutation = useSynchronizeIntegration({
    currentStatus: integrationStatusData?.status,
    onSuccess: () => {
      displayToastSuccess(strings.successToast);
    },
    onError: () => {
      displayToastError(strings.errorToast);
    },
  });

  const handleRefresh = () => {
    synchronizeMutation.mutate({ entities: [entityType] });
  };

  useEffect(() => {
    const isPending =
      synchronizeMutation.isPending ||
      integrationStatusData?.integrations?.some(integration =>
        integration.entities.some(
          entity =>
            entity.entityType == entityType &&
            entity.syncingStatus == IntegrationSyncingStatus.Syncing
        )
      ) == true;

    setIsRefreshing(isPending);
  }, [synchronizeMutation.isPending, integrationStatusData, entityType]);

  const [lastRefreshedAt, setLastRefreshedAt] = useState<Date | undefined>(undefined);
  useEffect(() => {
    const entityLastSyncedAtArray = integrationStatusData?.integrations
      ?.map(
        integration =>
          integration.entities.find(
            entity =>
              entity.entityType == entityType &&
              entity.syncingStatus == IntegrationSyncingStatus.Idle
          )?.lastSyncedAt
      )
      ?.filter((lastSyncedAt): lastSyncedAt is Date => {
        return lastSyncedAt != null;
      });

    const newLastRefreshedAt =
      entityLastSyncedAtArray && entityLastSyncedAtArray.length > 0
        ? new Date(Math.max(...entityLastSyncedAtArray.map(date => date.getTime())))
        : undefined;

    setLastRefreshedAt(newLastRefreshedAt);
  }, [entityType, integrationStatusData?.integrations]);

  return (
    <RefreshData
      buttonTitle={isRefreshing ? strings.buttonTitleRefreshing : strings.buttonTitle}
      dataUpdatedAt={lastRefreshedAt}
      description={
        <Trans
          i18nKey={strings.descriptionI18nKey}
          t={t}
          components={{
            refreshButton: <span className="font-medium text-foreground" />,
            bold: <span className="font-medium text-foreground" />,
          }}
          values={{
            refreshButton: strings.refreshButtonText,
          }}
        />
      }
      isPending={isRefreshing}
      onRefresh={handleRefresh}
      disabled={disabled}
    />
  );
};
